import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../utils/cn'
import type { TestIdProps } from '../internal/test-id'

/**
 * A busy indicator.
 *
 * Replaces a ThreeDotsLoader and a SpinLoader with one component, because
 * they differed only in appearance and the choice between them was never a
 * meaningful one for a caller to make.
 *
 * `label` is required: a spinner with no accessible name is announced as
 * nothing, so a screen reader user gets silence instead of "loading".
 */
export const spinnerVariants = cva('animate-spin rounded-full border-2 border-current border-t-transparent', {
    variants: {
        size: {
            small: 'size-3',
            medium: 'size-4',
            large: 'size-6'
        },
        tone: {
            /**
             * Takes the colour of whatever encloses it.
             *
             * For a spinner inside a control that already sets a colour per
             * variant and per state. A Button's spinner is drawn while the
             * button is disabled, so a fixed tone renders a near-black glyph on
             * the disabled surface.
             */
            inherit: 'text-inherit',
            default: 'text-foreground',
            muted: 'text-foreground-muted',
            onColor: 'text-foreground-brand'
        }
    },
    defaultVariants: { size: 'medium', tone: 'muted' }
})

export type SpinnerProps = TestIdProps &
    VariantProps<typeof spinnerVariants> & {
        /** Announced while busy, e.g. "Loading invoices". Always passed in. */
        label: string
    }

export const Spinner = ({ size, tone, label, 'data-testid': testId }: SpinnerProps) => (
    <span role="status" aria-live="polite" data-testid={testId} className="inline-flex items-center">
        <span aria-hidden className={cn(spinnerVariants({ size, tone }))} />
        <span className="sr-only">{label}</span>
    </span>
)
